import { defineStore, Store } from 'pinia'
import { FormContext, useForm } from 'vee-validate'
import { onMounted, onUnmounted } from 'vue'
import { openConfirmDialog } from '../useDialog'
import { i18n } from '../../localization'
import { EcspComplianceProfile, LegalAccountData, useLegalAccountDataState } from './useAccountDataLegal'
import { LegalAccountPage, navigateToNextPageWithMissingFieldsForInvestmentLegal } from './useAccountNavigationLegal'
import { getFieldRulesLegal } from '../../validation'
import { getDisableIbanBicValidation } from '../usePlatformSettings'
import { LegalEsacComplianceProfile } from '../../components/wizard/ESAC/legal/form/forms'

export interface LegalAccountFormStore {
    currentForm: FormContext
    hasValidationErrors?: boolean
    isDirty?: boolean
    submit?: () => unknown
}

export const useLegalAccountFormState = defineStore('legal-account-form', {
    state: () => {
        return {
            currentForm: null,
            missingDataProcessForVerificationActive: false,
        }
    },
    getters: {
        hasValidationErrors: (state) => (state.currentForm ? Object.keys(state.currentForm.errors).length > 0 : false),
        isDirty: (state) => state.currentForm && state.currentForm.meta.dirty,
    },
    actions: {
        async submit() {
            const legalAccountStore = useLegalAccountDataState()
            const { valid } = await this.currentForm.validate()

            if (valid) {
                const savedData = await legalAccountStore.saveAccountData()
                // when missingDataProcessForVerificationActive is set to true the user will be redirected to the next page with missing fields for investment an then back to verification
                if (this.missingDataProcessForVerificationActive) {
                    await navigateToNextPageWithMissingFieldsForInvestmentLegal('Verification')
                }
                return savedData
            }
        },
    },
})

export async function handleDirtyState(accountStore: Store<'legal-account-data', LegalAccountData>): Promise<boolean> {
    const { currentForm } = useLegalAccountFormState()

    // TODO: refactor duplicate code with useAccountForm.ts (personalAccount)
    return new Promise(async (resolve) => {
        let resolveVal = true
        let confirmed = false

        if (currentForm && currentForm.meta.dirty) {
            confirmed = await openConfirmDialog(
                i18n.global.t('dialog.formDirty.title'),
                i18n.global.t('dialog.formDirty.text'),
                {
                    type: 'Warning',
                    confirmText: i18n.global.t('dialog.formDirty.confirm'),
                    cancelText: i18n.global.t('dialog.formDirty.cancel'),
                }
            )
            if (confirmed) {
                if (!currentForm.meta.valid) {
                    openConfirmDialog(
                        i18n.global.t('dialog.formInvalid.title'),
                        i18n.global.t('dialog.formInvalid.text'),
                        {
                            type: 'Error',
                            hideCancel: true,
                        }
                    )
                    resolveVal = false
                } else {
                    accountStore.saveAccountData().catch(() => (resolveVal = false)) // TODO: maybe add additional tailwind alert
                }
            } else {
                currentForm.resetForm()

                const ecspKeys = Object.getOwnPropertyNames(new EcspComplianceProfile())
                const esacKeys = Object.getOwnPropertyNames(new LegalEsacComplianceProfile())
                const formValuesKeys = Object.getOwnPropertyNames(currentForm.values)

                if (currentForm.values.legal_account) {
                    Object.keys(currentForm.values.legal_account).forEach((key) => {
                        accountStore.$patch({ legalProfile: { [key]: currentForm.values.legal_account[key] } })
                    })
                }

                if (JSON.stringify(ecspKeys) === JSON.stringify(formValuesKeys)) {
                    Object.keys(currentForm.values).forEach((key) => {
                        accountStore.$patch({
                            legalProfile: {
                                compliance_profiles: {
                                    ecsp: { [key]: { ...currentForm.values[key] } },
                                },
                            },
                        })
                    })
                }

                if (JSON.stringify(esacKeys) === JSON.stringify(formValuesKeys)) {
                    Object.keys(currentForm.values).forEach((key) => {
                        accountStore.$patch({
                            legalProfile: {
                                compliance_profiles: {
                                    esac: { [key]: { ...currentForm.values[key] } },
                                },
                            },
                        })
                    })
                }
            }
        }
        resolve(resolveVal)
    })
}

export function getLegalAccountValidationSchema(validateInvestmentRequired = false): {
    // @ts-ignore
    [key: LegalAccountPage]: Record<string, string>
} {
    return {
        'Company Data': {
            'legal_account.company_name': getFieldRulesLegal(
                'company_name',
                ['min:2', 'max:255'],
                validateInvestmentRequired
            ),
            'legal_account.registration_number': getFieldRulesLegal(
                'registration_number',
                ['min:1', 'max:255'],
                validateInvestmentRequired
            ),
            'legal_account.registration_authority': getFieldRulesLegal(
                'registration_authority',
                ['min:1', 'max:255'],
                validateInvestmentRequired
            ),
            'legal_account.legal_form': getFieldRulesLegal(
                'legal_form',
                ['min:2', 'max:255'],
                validateInvestmentRequired
            ),
            'legal_account.description': getFieldRulesLegal(
                'description',
                ['min:3', 'max:255'],
                validateInvestmentRequired
            ),
        },
        'Address Data': {
            'legal_account.address': getFieldRulesLegal('address', ['min:7', 'max:255'], validateInvestmentRequired),
            'legal_account.postal_code': getFieldRulesLegal(
                'postal_code',
                ['min:2', 'max:255'],
                validateInvestmentRequired
            ),
            'legal_account.city': getFieldRulesLegal('city', ['min:2', 'max:255'], validateInvestmentRequired),
            'legal_account.state': getFieldRulesLegal('state', ['min:2', 'max:255'], validateInvestmentRequired),
            'legal_account.country': getFieldRulesLegal('country', ['countryISO3166'], validateInvestmentRequired),
        },
        'Bank Data': {
            'legal_account.iban': getFieldRulesLegal(
                'iban',
                getDisableIbanBicValidation() ? ['min:3', 'max:255'] : ['iban'],
                validateInvestmentRequired
            ),
            'legal_account.bic': getFieldRulesLegal(
                'bic',
                getDisableIbanBicValidation() ? ['min:3', 'max:255'] : ['bic'],
                validateInvestmentRequired
            ),
        },
        'Tax Data': {
            'legal_account.tax_number': getFieldRulesLegal(
                'tax_number',
                ['min:2', 'max:255'],
                validateInvestmentRequired
            ),
        },
    }
}

export function initLegalAccountForm(page: LegalAccountPage) {
    const { legalProfile: legal_account } = useLegalAccountDataState()

    const form = useForm({
        validationSchema: getLegalAccountValidationSchema()[page],
        initialValues: { legal_account },
    })
    const formState = useLegalAccountFormState()

    onMounted(() => {
        // @ts-ignore
        formState.currentForm = form
        formState.currentForm.resetForm()
    })
    onUnmounted(() => {
        formState.currentForm = null
    })
}
